import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, RefreshCw, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { useCache } from '../../contexts/CacheContext';
import { deleteEvent, fetchEventsByLawyerID, createEvent, updateEvent as updateEventService } from '../../services/eventService';
import { EventFormData, ViewMode, Notification as NotificationType } from './types';
import { Event } from '../../types';
import { EventList, EventFilters, Notification, EventDetailsPanel } from './components';
import { SectionHeader } from '../../components/ui';
import { AddEventModal, EditEventModal, EventDetailsModal } from './modals';
import { CalendarView } from './views';

const Events: React.FC = () => {
  const { user } = useAuth();
  const { strings, language, isRTL } = useLocalization();
  const { 
    presetData, 
    getEventTypeName: getEventTypeNameWithLang, 
    getPlaceTypeName: getPlaceTypeNameWithLang
  } = useSystemPreset();

  // Wrapper functions for components that expect single-parameter functions
  const getEventTypeName = useCallback((typeId: string) => getEventTypeNameWithLang(typeId, language), [getEventTypeNameWithLang, language]);
  const getPlaceTypeName = useCallback((typeId: string) => getPlaceTypeNameWithLang(typeId, language), [getPlaceTypeNameWithLang, language]);
  const { 
    getEvents, 
    getOffices, 
    getClients, 
    getLegalFiles,
    addEvent,
    updateEvent,
    removeEvent,
    isLoaded: cacheLoaded
  } = useCache();
  
  // State
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [notification, setNotification] = useState<NotificationType | null>(null);
  const [currentView, setCurrentView] = useState<'list' | 'details'>('list');

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Load events from cache
  const loadEventsFromCache = useCallback(() => {
    if (cacheLoaded) {
      const cachedEvents = getEvents({ status: filterStatus });
      setEvents(cachedEvents);
      setLoading(false);
    }
  }, [cacheLoaded, filterStatus]);

  // Refresh events from server
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.id) {
        setEvents([]);
        return;
      }

      const fetchedEvents = await fetchEventsByLawyerID(user.id);
      console.log('📅 Fetched events from server:', fetchedEvents.length);
      console.log('📅 Sample event:', fetchedEvents[0]);
      console.log('📅 Event date fields:', fetchedEvents.slice(0, 3).map(e => ({
        id: e.id,
        title: e.title || e.legalFileTitle,
        startDate: e.startDate,
        date: e.date,
        endDate: e.endDate
      })));
      setEvents(fetchedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Event handlers
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEvents();
    setRefreshing(false);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleStatusFilterChange = (status: string) => {
    setFilterStatus(status);
  };

  const showNotification = (type: 'success' | 'error' | 'deletion', message: string) => {
    setNotification({ type, message });
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      setDeletingEventId(eventId);
      await deleteEvent(eventId);
      
      removeEvent(eventId);
      setEvents(prev => prev.filter(event => event.id !== eventId));
      
      // Close modal if the deleted event was selected
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
      }
      
      showNotification('deletion', language === 'ar' ? 'تم حذف الحدث بنجاح' : 'Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      showNotification('error', language === 'ar' ? 'فشل في حذف الحدث' : 'Failed to delete event');
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleEditEvent = (event: Event) => {
      setEditingEvent(event);
    setShowEditModal(true);
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setCurrentView('details');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedEvent(null);
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredEvents.map(event => ({
        'Event ID': event.id,
        'Title': event.title || event.legalFileTitle || 'Untitled Event',
        'Description': event.description || 'N/A',
        'Event Type': event.eventTypeID ? getEventTypeName(event.eventTypeID) : 'N/A',
        'Place Type': event.placeTypeID ? getPlaceTypeName(event.placeTypeID) : 'N/A',
        'Place': event.location || event.placeName || 'N/A',
        'Start Date': event.startDate ? new Date(event.startDate).toLocaleDateString() : 'N/A',
        'End Date': event.endDate ? new Date(event.endDate).toLocaleDateString() : 'N/A',
        'Time': event.time || 'N/A',
        'Status': event.status || 'N/A',
        'Created At': event.createdAt ? new Date(event.createdAt).toLocaleString() : 'N/A',
        'Updated At': event.updatedAt ? new Date(event.updatedAt).toLocaleString() : 'N/A',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'Events');
      XLSX.writeFile(wb, `events_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting events to Excel:', err);
      showNotification('error', language === 'ar' ? 'فشل في تصدير الأحداث' : 'Failed to export events to Excel.');
    }
  };

  const handleCreateEvent = async (formData: EventFormData) => {
    try {
      setCreateLoading(true);
      const eventId = await createEvent(formData);
      
      // Create the event object with the returned ID
      const newEvent: Event = {
        ...formData,
        id: eventId,
        title: formData.title || formData.legalFileTitle || 'Untitled Event',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      addEvent(newEvent);
      setEvents(prev => [...prev, newEvent]);
      
      showNotification('success', language === 'ar' ? 'تم إنشاء الحدث بنجاح' : 'Event created successfully');
    } catch (error) {
      console.error('Error creating event:', error);
      showNotification('error', language === 'ar' ? 'فشل في إنشاء الحدث' : 'Failed to create event');
      throw error;
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateEvent = async (eventId: string, formData: EventFormData) => {
    try {
      setUpdateLoading(true);
      await updateEventService(eventId, formData);
      
      // Create the updated event object
      const updatedEvent: Event = {
        ...formData,
        id: eventId,
        title: formData.title || formData.legalFileTitle || 'Untitled Event',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      updateEvent(eventId, updatedEvent);
      setEvents(prev => prev.map(event => event.id === eventId ? { ...event, ...updatedEvent } : event));
      
      showNotification('success', language === 'ar' ? 'تم تحديث الحدث بنجاح' : 'Event updated successfully');
    } catch (error) {
      console.error('Error updating event:', error);
      showNotification('error', language === 'ar' ? 'فشل في تحديث الحدث' : 'Failed to update event');
      throw error;
    } finally {
      setUpdateLoading(false);
    }
  };

  // Filtered events
  const filteredEvents = useMemo(() => {
    let filtered = events;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event =>
        (event.legalFileTitle?.toLowerCase().includes(query)) ||
        (event.clientName?.toLowerCase().includes(query)) ||
        (event.notes?.toLowerCase().includes(query)) ||
        (event.eventTypeID ? getEventTypeName(event.eventTypeID).toLowerCase().includes(query) : false)
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      const statusValue = parseInt(filterStatus);
      filtered = filtered.filter(event => event.status === statusValue);
    }

    return filtered;
  }, [events, searchQuery, filterStatus, getEventTypeName]);


  // Load data on mount
  useEffect(() => {
    if (cacheLoaded) {
    loadEventsFromCache();
    } else {
      fetchEvents();
    }
  }, [cacheLoaded, loadEventsFromCache, fetchEvents]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
            <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
            {language === 'ar' ? 'جاري تحميل الأحداث' : 'Loading Events'}
          </h2>
          <p className="text-slate-600 dark:text-slate-300">
            {language === 'ar' ? 'جاري جلب أحداثك...' : 'Fetching your events...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
              </div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
            {language === 'ar' ? 'خطأ في تحميل الأحداث' : 'Error Loading Events'}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
              <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
            {language === 'ar' ? 'حاول مرة أخرى' : 'Try Again'}
              </button>
            </div>
          </div>
    );
  }
        
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={language === 'ar' ? 'الأحداث' : 'Events'}
          description={language === 'ar' ? 'إدارة أحداثك ومواعيدك' : 'Manage your events and appointments'}
          language={language}
          isRTL={isRTL}
          actionButtons={[
            {
              label: language === 'ar' ? 'تصدير إكسل' : 'Export Excel',
              onClick: exportToExcel,
              icon: <Download className="w-4 h-4" />,
              variant: 'secondary',
              requiresProfessional: false
            }
          ]}
          refreshButton={{
            onClick: handleRefresh,
            disabled: refreshing,
            loading: refreshing
          }}
          addButton={{
            label: language === 'ar' ? 'إضافة حدث' : 'Add Event',
            onClick: () => setShowCreateModal(true)
          }}
        >
          <EventFilters
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onClearSearch={handleClearSearch}
            statusFilter={filterStatus}
            onStatusFilterChange={handleStatusFilterChange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            language={language}
            isRTL={isRTL}
            strings={strings}
          />
        </SectionHeader>

        {/* Content */}
        {viewMode === 'calendar' ? (
          <CalendarView
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            events={filteredEvents}
            onEventClick={handleEventClick}
            language={language}
            strings={strings}
            getEventTypeName={getEventTypeName}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[800px]">
            {/* Left Panel - Events List */}
            <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? `الأحداث (${filteredEvents.length})` : `Events (${filteredEvents.length})`}
                  </h2>
                </div>
                
                {/* Events List */}
                <div className="flex-1 overflow-y-auto p-6">
                  <EventList
                    events={filteredEvents}
                    onEventClick={handleEventClick}
                    selectedEventId={selectedEvent?.id || null}
                    language={language}
                    strings={strings}
                    getEventTypeName={getEventTypeName}
                    getPlaceTypeName={getPlaceTypeName}
                  />
                </div>
              </div>
            </div>

            {/* Right Panel - Event Details */}
            <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <EventDetailsPanel
                event={selectedEvent}
                onBack={handleBackToList}
                onEdit={handleEditEvent}
                onDelete={handleDeleteEvent}
                language={language}
                strings={strings}
                getEventTypeName={getEventTypeName}
                getPlaceTypeName={getPlaceTypeName}
                isDeleting={deletingEventId === selectedEvent?.id}
              />
            </div>
          </div>
        )}

        {/* Modals */}
        <AddEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateEvent}
          loading={createLoading}
          offices={getOffices()}
          clients={getClients()}
          legalFiles={getLegalFiles()}
          eventTypes={presetData?.eventTypes || []}
          placeTypes={presetData?.placeTypes || []}
          courts={presetData?.courts || []}
          language={language}
          strings={strings}
        />

        <EditEventModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingEvent(null);
          }}
          onSubmit={handleUpdateEvent}
          event={editingEvent}
          loading={updateLoading}
          offices={getOffices()}
          clients={getClients()}
          legalFiles={getLegalFiles()}
          eventTypes={presetData?.eventTypes || []}
          placeTypes={presetData?.placeTypes || []}
          courts={presetData?.courts || []}
          language={language}
          strings={strings}
        />

        {/* Event Details Modal for Calendar View */}
        {viewMode === 'calendar' && (
          <EventDetailsModal
            isOpen={selectedEvent !== null}
            onClose={() => setSelectedEvent(null)}
            event={selectedEvent}
            language={language}
            strings={strings}
            getEventTypeName={getEventTypeName}
            getPlaceTypeName={getPlaceTypeName}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            isDeleting={deletingEventId === selectedEvent?.id}
          />
        )}

        {/* Notification */}
        <Notification
          notification={notification}
          onClose={() => setNotification(null)}
          language={language}
        />
      </div>
    </div>
  );
};

export default Events;